import React from 'react'

import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Grid } from 'react-styled-flexboxgrid'

import { TRIBE_PRICING_PAGE_METRIC_SUMMARY_UTM } from '@constants'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { selectIsPriceHidden } from '@store/campaigns'
import {
  selectPostsAvgReachPct,
  selectPostsOverallCPE,
  selectPostsOverallCPM,
  selectPostsTotalComments,
  selectPostsTotalEngagement,
  selectPostsTotalEngagementPct,
  selectPostsTotalFollowers,
  selectPostsTotalImpressions,
  selectPostsTotalLikes,
  selectPostsTotalPosts,
  selectPostsTotalReach,
  selectPostsTotalSpend,
} from '@store/postCampaignReport'
import { selectCampaignId } from '@store/router'
import AnalyticsPostCampaignReportMetric from '../Metric/Metric'
import { StyledCol, StyledRow } from '../Summary/Summary.styled'

const externalLink = TRIBE_PRICING_PAGE_METRIC_SUMMARY_UTM
const productMetricGroup = 'reporting_instagram_posts'

const AnalyticsPostCampaignReportPost: React.FC = () => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match)!
  const channel = PCRChannels.IGPosts

  const totalPosts = useSelector((state: IApplicationState) =>
    selectPostsTotalPosts(state, campaignId, channel),
  )
  const totalSpend = useSelector((state: IApplicationState) =>
    selectPostsTotalSpend(state, campaignId, channel),
  )
  const totalFollowers = useSelector((state: IApplicationState) =>
    selectPostsTotalFollowers(state, campaignId, channel),
  )
  const totalEngagement = useSelector((state: IApplicationState) =>
    selectPostsTotalEngagement(state, campaignId, channel),
  )
  const totalEngagementPct = useSelector((state: IApplicationState) =>
    selectPostsTotalEngagementPct(state, campaignId, channel),
  )
  const totalLikes = useSelector((state: IApplicationState) =>
    selectPostsTotalLikes(state, campaignId, channel),
  )
  const totalComments = useSelector((state: IApplicationState) =>
    selectPostsTotalComments(state, campaignId, channel),
  )
  const overallCPE = useSelector((state: IApplicationState) =>
    selectPostsOverallCPE(state, campaignId, channel),
  )
  const avgReachPct = useSelector((state: IApplicationState) =>
    selectPostsAvgReachPct(state, campaignId, channel),
  )
  const totalReach = useSelector((state: IApplicationState) =>
    selectPostsTotalReach(state, campaignId, channel),
  )
  const totalImpressions = useSelector((state: IApplicationState) =>
    selectPostsTotalImpressions(state, campaignId, channel),
  )
  const overallCPM = useSelector((state: IApplicationState) =>
    selectPostsOverallCPM(state, campaignId, channel),
  )
  const isPriceHidden = useSelector((state: IApplicationState) =>
    selectIsPriceHidden(state, campaignId),
  )

  return (
    <Grid fluid>
      <StyledRow>
        <StyledCol xs={6} lg={3} data-testid="total-posts-cell">
          <AnalyticsPostCampaignReportMetric title="ig.post.totalPost" value={totalPosts} />
        </StyledCol>
        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="total-spend-cell">
            <AnalyticsPostCampaignReportMetric
              title="ig.post.totalSpend"
              value={totalSpend.amount}
              currency={totalSpend.currency}
            />
          </StyledCol>
        )}
        <StyledCol xs={6} lg={3} data-testid="total-followers-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.totalFollowers"
            value={totalFollowers}
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-engagement-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.totalEngagement"
            value={totalEngagement}
          />
        </StyledCol>
      </StyledRow>
      <StyledRow>
        <StyledCol xs={6} lg={3} data-testid="total-likes-cell">
          <AnalyticsPostCampaignReportMetric title="ig.post.likes" value={totalLikes} />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-comments-cell">
          <AnalyticsPostCampaignReportMetric title="ig.post.comments" value={totalComments} />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-engagement-pct-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.totalEngagementPct"
            value={totalEngagementPct}
            showPercentSign
          />
        </StyledCol>

        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="overall-cpe-cell">
            <AnalyticsPostCampaignReportMetric
              title="ig.post.overallCPE"
              value={overallCPE.amount}
              currency={overallCPE.currency}
            />
          </StyledCol>
        )}
      </StyledRow>
      <StyledRow>
        <StyledCol xs={6} lg={3} data-testid="total-reach-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.totalReach"
            value={totalReach}
            externalLink={externalLink}
            productMetricGroup={productMetricGroup}
            productMetricValue="total_reach"
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="avg-reach-pct-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.avgReachPct"
            value={avgReachPct}
            showPercentSign
            externalLink={externalLink}
            productMetricGroup={productMetricGroup}
            productMetricValue="avg_reach_pct"
          />
        </StyledCol>
        <StyledCol xs={6} lg={3} data-testid="total-impressions-cell">
          <AnalyticsPostCampaignReportMetric
            title="ig.post.totalImpressions"
            value={totalImpressions}
            externalLink={externalLink}
            productMetricGroup={productMetricGroup}
            productMetricValue="total_impressions"
          />
        </StyledCol>
        {!isPriceHidden && (
          <StyledCol xs={6} lg={3} data-testid="overall-cpm-cell">
            <AnalyticsPostCampaignReportMetric
              title="ig.post.overallCPM"
              value={overallCPM && overallCPM.amount}
              currency={overallCPM && overallCPM.currency}
              externalLink={externalLink}
              productMetricGroup={productMetricGroup}
              productMetricValue="overall_cpm"
            />
          </StyledCol>
        )}
      </StyledRow>
    </Grid>
  )
}

export { AnalyticsPostCampaignReportPost }
export default AnalyticsPostCampaignReportPost
